import { Pencil, Trash2, Shield, User } from "lucide-react";
import PresenceDot from "@/components/PresenceDot";
import { presenceTooltip, relativeTime } from "@/lib/presence";
import { classLabel } from "@/pages/teacher/teacherUtils";

export default function TeachersTable({ loading, teachers, filteredTeachers, query, isReadOnly, user, openEdit, setDelTarget }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {loading ? (
        <div className="p-10 text-center text-slate-500">Loading…</div>
      ) : teachers.length === 0 ? (
        <div className="p-10 text-center text-slate-500">No teachers yet.</div>
      ) : filteredTeachers.length === 0 ? (
        <div className="p-10 text-center text-slate-500" data-testid="teacher-search-empty">No teachers match "{query}".</div>
      ) : (
        <table className="w-full text-sm" data-testid="teachers-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Phone</th>
              <th className="px-4 py-3 text-left">Class</th>
              <th className="px-4 py-3 text-left">Subject</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Assignments</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredTeachers.map((t) => (
              <tr key={t.id} data-testid={`teacher-row-${t.email}`}>
                <td className="px-4 py-3">
                  <div
                    className="flex items-center gap-2.5 cursor-default"
                    title={presenceTooltip({ status: t.presence, lastSeenAt: t.last_seen_at, lastLoginAt: t.last_login_at })}
                    data-testid={`teacher-presence-${t.email || t.phone || t.id}`}
                  >
                    <PresenceDot status={t.presence || "offline"} lastSeenAt={t.last_seen_at} />
                    <div className="leading-tight">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{t.name}</div>
                      <div className="text-[11px] text-slate-400">
                        {t.presence === "online" ? "Online now" : t.last_login_at ? `Last login ${relativeTime(t.last_login_at)}` : "Never logged in"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{t.phone || "—"}</td>
                <td className="px-4 py-3" data-testid={`teacher-class-${t.email}`}>
                  {(t.class_teacher_of && t.class_teacher_of.length > 0) ? (
                    <div className="flex flex-wrap gap-1">
                      {t.class_teacher_of.map((a) => (
                        <span key={`${a.class_name}${a.section}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
                          <Shield className="h-3 w-3" /> {classLabel(a.class_name)}-{a.section}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{(t.subjects && t.subjects.length ? t.subjects.join(", ") : t.subject) || "—"}</td>
                <td className="px-4 py-3">
                  {t.role === "super_admin" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200">
                      <Shield className="h-3 w-3" /> Super admin
                    </span>
                  ) : t.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
                      <Shield className="h-3 w-3" /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200">
                      <User className="h-3 w-3" /> Teacher
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {t.role === "admin" || t.role === "super_admin" ? (
                    <span className="text-xs text-slate-400">— full access —</span>
                  ) : (t.assignments || []).length === 0 ? (
                    <span className="text-xs text-rose-600">none</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {t.assignments.map((a) => (
                        <span key={`${a.class_name}${a.section}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">
                          {classLabel(a.class_name)}-{a.section}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => openEdit(t)}
                      disabled={isReadOnly || t.role === "super_admin"}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`edit-teacher-${t.email}`}
                      title={isReadOnly ? "Read-only" : t.role === "super_admin" ? "Super admin cannot be edited" : "Edit"}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDelTarget(t)}
                      disabled={isReadOnly || t.id === user?.id || t.role === "super_admin"}
                      className="p-2 rounded-lg hover:bg-rose-50 text-rose-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-teacher-${t.email}`}
                      title={isReadOnly ? "Read-only" : t.role === "super_admin" ? "Super admin cannot be deleted" : t.id === user?.id ? "Cannot delete yourself" : "Delete"}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
